import {createLogger} from 'logger';
import {getWarnOnce, timeout} from './utils';

export enum PixelEventType {
    AddPaymentInfo = 'AddPaymentInfo',
    AddToCart = 'AddToCart',
    AddToWishlist = 'AddToWishlist',
    CompleteRegistration = 'CompleteRegistration',
    Contact = 'Contact',
    CustomizeProduct = 'CustomizeProduct',
    Donate = 'Donate',
    FindLocation = 'FindLocation',
    InitiateCheckout = 'InitiateCheckout',
    Lead = 'Lead',
    PageView = 'PageView',
    Purchase = 'Purchase',
    Schedule = 'Schedule',
    Search = 'Search',
    StartTrial = 'StartTrial',
    SubmitApplication = 'SubmitApplication',
    Subscribe = 'Subscribe',
    ViewContent = 'ViewContent',
}

interface PixelConfig {
    id: number;
    host: string;
}

type PixelCommand = 'track' | 'trackCustom';

export interface PixelEvent {
    command: PixelCommand;
    event: PixelEventType | string;
    data?: Object;
}

export class Pixel {
    private warnOnce = getWarnOnce(createLogger('pixel'));
    private pixelConfig?: PixelConfig;

    async isInitialized() {
        let initialized = false;

        const waitForInitListener = ({data, origin}: MessageEvent) => {
            if (origin !== this.pixelConfig?.host) {
                return;
            }
            if (data === 'pong') {
                initialized = true;
                removeEventListener('message', waitForInitListener);
            }
        };

        window.addEventListener('message', waitForInitListener, false);
        let attempts = 10;

        // eslint-disable-next-line no-unmodified-loop-condition
        while (!initialized && attempts > 0) {
            attempts--;
            this.sendMessage('ping', false);
            await timeout(100);
        }
        return initialized;
    }

    initPixel(config: PixelConfig) {
        this.pixelConfig = config;
    }

    trackStandard(event: PixelEventType, data?: Object) {
        const events = Array.isArray(event) ? event : [event];
        events.forEach((eventType) => this.sendCommand('track', eventType, data));
    }

    trackCustom(event: string, data?: Object) {
        const events = Array.isArray(event) ? event : [event];
        events.forEach((eventType) => this.sendCommand('trackCustom', eventType, data));
    }

    track(trackEvents: PixelEvent[] | PixelEvent | string[] | string) {
        const messages = Array.isArray(trackEvents) ? trackEvents : [trackEvents];
        messages.forEach((message) =>
            typeof message === 'string' ? this.trackCustom(message) : this.sendMessage(message),
        );
    }

    private async sendMessage(message: PixelEvent | string, waitForInit = true) {
        const pixelFrame = (document?.getElementById('pixel') as HTMLIFrameElement)?.contentWindow;

        // todo postpone messages if pixel is not initialized?
        if (!this.pixelConfig || !pixelFrame || (waitForInit && !(await this.isInitialized()))) {
            this.warnOnce('pixel');
            return;
        }

        pixelFrame.postMessage(message, this.pixelConfig.host);
    }

    private async sendCommand(
        command: PixelCommand,
        event: PixelEventType | string,
        data?: Object,
    ) {
        await this.sendMessage({command, event, data});
    }
}

const pixel = new Pixel();

export default pixel;

export const createPixel = (config: PixelConfig) => {
    pixel.initPixel(config);
};
